import {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
	KeyboardEvent,
	MouseEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import Icon, { IconType } from 'components/Icon';
import useScrollMemory from 'hooks/useScrollMemory';
import useDsNavigation from 'hooks/useDsNavigation';
import { DsTabKind, DsLink } from 'designSystem/types';
import { DS_TAB_CHROME } from 'designSystem/util';
import { useTheme } from 'styled-components';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { setDsTitle } from 'redux/db/actions';
import { designSystemEditingBlocked } from 'redux/db/util';
import MarkdownEditor from 'components/Widget/Input/MarkdownEditor';
import forge from 'apis/forge';
import * as S from './styles';

const SECTION_DESCRIPTIONS: Partial<Record<DsTabKind, string>> = {
	foundations:
		'The underlying building blocks of the system. Choices that everything else is built on.',

	guidelines:
		'Guidelines tell the AI how to compose screens — the more specific you are, the more magical the results.',

	colors:
		'The full color story — primary palette and the named colors used throughout the system.',

	typography:
		'Fonts, type styles, and the scale of sizes and weights used across the system.',

	styles:
		'Spacing, shape, depth, motion, and the named recipes that combine them into reusable looks.',

	components:
		'Reusable interface elements, designed to be consistent, accessible, and ready to use.',

	gallery: 'Real examples that show the system in action across screens and layouts.',

	assets:
		'Brand and visual materials: logos, icon sets, illustrations, and other supporting resources.',
};

const SAVE_DEBOUNCE_MS = 1500;

const HomeView = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { openTab } = useDsNavigation();
	const scrollRef = useScrollMemory('home');
	const [localTitle, setLocalTitle] = useState('');
	const descSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingDescSaveRef = useRef<(() => void) | null>(null);

	const { designSystemContent, activeDesignSystem, activeDesignSystemId, dsTitle } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemId: r.mode.activeDesignSystemId,
			dsTitle: r.db.dsTitle,
		}),
		shallowEqual
	);

	const description = designSystemContent?.config?.meta?.description ?? '';

	// Use dsTitle (DS-version poll), not activeDesignSystem.title — for first-time guest
	// viewers the latter lags behind until the session poll registers the DS.
	useEffect(() => {
		setLocalTitle(dsTitle);
	}, [activeDesignSystemId, dsTitle]);

	// Flush pending description save on DS switch or unmount
	useEffect(() => {
		return () => {
			if (descSaveTimerRef.current) clearTimeout(descSaveTimerRef.current);
			pendingDescSaveRef.current?.();
		};
	}, [activeDesignSystemId]);

	//----------------------------------------------------------------------
	// DATA
	//----------------------------------------------------------------------
	const guidelines = useMemo(() => {
		return (designSystemContent?.guidelines || []).map((g) => ({
			label: g.title,
			contentPath: g.path,
		}));
	}, [designSystemContent?.guidelines]);

	const components = useMemo(() => {
		return (designSystemContent?.components?.all || []).map((c) => ({
			label: c.name,
			contentPath: c.id,
		}));
	}, [designSystemContent?.components?.all]);

	const gallery = useMemo(() => {
		return (designSystemContent?.gallery || []).map((g) => ({
			label: g.title,
			contentPath: g.path,
		}));
	}, [designSystemContent?.gallery]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onTitleBlur = useCallback(() => {
		if (!activeDesignSystemId || !activeDesignSystem) return;
		const trimmed = localTitle.trim();
		if (!trimmed || trimmed === dsTitle) {
			setLocalTitle(dsTitle);
			return;
		}
		if (designSystemEditingBlocked()) {
			setLocalTitle(dsTitle);
			return;
		}
		// Optimistic update for the title bar (which reads r.db.dsTitle, not the optimistic DS write).
		dispatch(setDsTitle(trimmed));
		forge.updateDesignSystem({ update: { id: activeDesignSystemId, title: trimmed } });
	}, [activeDesignSystemId, activeDesignSystem, localTitle, dsTitle, dispatch]);

	const onTitleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.currentTarget.blur();
			} else if (e.key === 'Escape') {
				setLocalTitle(dsTitle);
				const el = e.currentTarget;
				setTimeout(() => el.blur(), 0);
			}
		},
		[dsTitle]
	);

	const onDescriptionChange = useCallback(
		(markdown: string) => {
			if (!activeDesignSystemId) return;
			if (descSaveTimerRef.current) clearTimeout(descSaveTimerRef.current);
			if (markdown === description) {
				pendingDescSaveRef.current = null;
				return;
			}
			const save = () => {
				pendingDescSaveRef.current = null;
				forge.saveDsFiles({
					designSystemId: activeDesignSystemId,
					files: [
						{
							path: 'config/meta.json',
							content: JSON.stringify({ description: markdown }, null, 2),
						},
					],
				});
			};
			pendingDescSaveRef.current = save;
			descSaveTimerRef.current = setTimeout(save, SAVE_DEBOUNCE_MS);
		},
		[activeDesignSystemId, description]
	);

	const isCreatingGuidelineRef = useRef(false);

	const onNewGuideline = useCallback(async () => {
		if (isCreatingGuidelineRef.current) return;
		if (!activeDesignSystemId || designSystemEditingBlocked()) return;
		isCreatingGuidelineRef.current = true;
		try {
			const existingPaths = new Set((designSystemContent?.guidelines || []).map((g) => g.path));
			let path = 'guidelines/untitled.md';
			let counter = 2;
			while (existingPaths.has(path)) {
				path = `guidelines/untitled-${counter}.md`;
				counter++;
			}
			const content =
				'## Overview\n\nDescribe the purpose and scope of this guideline.\n\n## Guidance\n\nAdd your recommendations here.\n';
			await forge.updateDsFiles({
				designSystemId: activeDesignSystemId,
				files: [{ path, content }],
			});
		} finally {
			isCreatingGuidelineRef.current = false;
		}
	}, [activeDesignSystemId, designSystemContent?.guidelines]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const isEditable = !designSystemEditingBlocked();

	// Cmd-click (or Ctrl-click) opens the tab in the background;
	// middle-click does the same via onAuxClick.
	const tabClickProps = (section: DsTabKind, contentPath?: string) => ({
		onClick: (e: MouseEvent) => openTab(section, contentPath, e.metaKey || e.ctrlKey),
		onAuxClick: (e: MouseEvent) => {
			if (e.button === 1) {
				e.preventDefault();
				openTab(section, contentPath, true);
			}
		},
	});

	const renderFeaturedCard = (section: DsTabKind) => {
		const chrome = DS_TAB_CHROME[section];
		const description = SECTION_DESCRIPTIONS[section];

		return (
			<S.FeaturedCard {...tabClickProps(section)}>
				<S.FeaturedTitle>
					<Icon type={chrome.icon} width={22} height={22} color={theme.gray[600]} />
					{chrome.label}
				</S.FeaturedTitle>
				{description && <S.FeaturedDescription>{description}</S.FeaturedDescription>}
				<S.CtaBadge>
					View {chrome.label.toLowerCase()}
					<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
				</S.CtaBadge>
			</S.FeaturedCard>
		);
	};

	const renderGuidelinesSection = () => {
		const chrome = DS_TAB_CHROME['guidelines'];
		const description = SECTION_DESCRIPTIONS.guidelines;
		return (
			<S.SectionCard>
				<S.SectionTitle>
					<Icon type={chrome.icon} width={20} height={20} color={theme.gray[600]} />
					{chrome.label}
				</S.SectionTitle>
				{description && <S.SectionIntro>{description}</S.SectionIntro>}
				<S.ItemsList>
					{guidelines.map((link) => (
						<S.ItemLink
							key={link.contentPath || link.label}
							{...tabClickProps('guidelines', link.contentPath)}
						>
							<span>{link.label}</span>
							<S.ArrowIcon>
								<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
							</S.ArrowIcon>
						</S.ItemLink>
					))}
				</S.ItemsList>
				{!designSystemEditingBlocked() && (
					<S.NewItemLink onClick={onNewGuideline}>
						<Icon type={IconType.ADD} width={14} height={14} />
						New guideline
					</S.NewItemLink>
				)}
			</S.SectionCard>
		);
	};

	const renderGridSection = (
		section: DsTabKind,
		links: DsLink[],
		allLinkLabel?: string
	) => {
		if (links.length === 0) return null;
		const chrome = DS_TAB_CHROME[section];
		const description = SECTION_DESCRIPTIONS[section];

		return (
			<S.SectionCard>
				<S.SectionTitle>
					<Icon type={chrome.icon} width={20} height={20} color={theme.gray[600]} />
					{chrome.label}
				</S.SectionTitle>
				{description && <S.SectionIntro>{description}</S.SectionIntro>}
				{allLinkLabel && (
					<S.AllItemsLink {...tabClickProps(section)}>
						<span>{allLinkLabel}</span>
						<S.AllLinkArrow>
							<Icon type={IconType.LINE_ARROW_RIGHT} width={12} height={12} />
						</S.AllLinkArrow>
					</S.AllItemsLink>
				)}
				<S.ItemsGrid>
					{links.map((link) => (
						<S.ItemTile
							key={link.contentPath || link.label}
							{...tabClickProps(section, link.contentPath)}
						>
							{link.label}
						</S.ItemTile>
					))}
				</S.ItemsGrid>
			</S.SectionCard>
		);
	};

	return (
		<S.Container ref={scrollRef}>
			<S.Content>
				<S.Hero>
					<S.HeroContent>
						<S.TitleInput
							value={localTitle}
							onChange={(e) => setLocalTitle(e.target.value)}
							onBlur={onTitleBlur}
							onKeyDown={onTitleKeyDown}
							readOnly={!isEditable}
							placeholder="Design System"
						/>
						{designSystemContent && (
							<S.DescriptionEditor>
								<MarkdownEditor
									content={description}
									editable={isEditable}
									onChange={onDescriptionChange}
									placeholder="Describe your design system..."
								/>
							</S.DescriptionEditor>
						)}
					</S.HeroContent>
				</S.Hero>
				{renderFeaturedCard('foundations')}
				{renderGuidelinesSection()}
				{renderFeaturedCard('colors')}
				{renderFeaturedCard('typography')}
				{renderFeaturedCard('styles')}
				{renderGridSection('components', components, 'All Components')}
				{renderGridSection('gallery', gallery, 'Complete Gallery')}
				{renderFeaturedCard('assets')}
			</S.Content>
		</S.Container>
	);
};

export default HomeView;
